import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

import { TYPE_STYLES } from '../styles/typography';

/**
 * The design-system `text-{style}` size tokens (`text-h3`, `text-b3`, …)
 * emitted by the Tailwind preset from `typography.json`.
 *
 * tailwind-merge must be taught these are FONT-SIZE utilities: its default
 * heuristic can't tell a custom `text-h3` from a text-COLOR class, so it
 * lumps both into the colour conflict group and "deduplicates" them —
 * `cn('text-secondary-600 … text-h3')` silently dropped the colour, and
 * `cn('… text-h3 … text-primary-600')` silently dropped the SIZE (the bug
 * behind mismatched menu-row typography). Consumer apps bundle their own
 * tailwind-merge, so each app's `cn` must extend with this same list —
 * import it from `@flashka/shared/ui` rather than redeclaring.
 */
export const FONT_SIZE_TOKENS = Object.keys(TYPE_STYLES);

/**
 * Custom border-radius tokens emitted by the preset (`rounded-toast-sm`,
 * `rounded-toast-md`). tailwind-merge must be taught these belong to the
 * all-corners `rounded` group, otherwise it can't dedupe them against a
 * later `rounded-[20px]` override — both survive and the CSS cascade picks a
 * winner by source order (the bug behind the conversational Toast keeping its
 * size-preset radius). Corner-specific overrides like `rounded-br-[8px]` are a
 * DIFFERENT group and still coexist, exactly as intended.
 */
export const BORDER_RADIUS_TOKENS = ['toast-sm', 'toast-md'];

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{ text: FONT_SIZE_TOKENS }],
      rounded: [{ rounded: BORDER_RADIUS_TOKENS }],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
